import { useEffect } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { useItemService } from "./useServices";
import { restError } from "@/models/client/RestResponse";
import { shouldRetryQuery } from "@/lib/query/retry";
import { logger } from "@/lib/logger";
import {
  WorkspaceContentSearchResult,
  WorkspaceContentSearchWorkspace,
} from "@/models/workspace/WorkspaceContentSearch";

/** A search this short would match almost everything; the input shows nothing until it is longer. */
export const CONTENT_SEARCH_MIN_LENGTH = 2;

/** The API's cap on hits per workspace (zooming into one workspace, the files tab). */
export const CONTENT_SEARCH_DOCUMENTS_CAP = 200;

export const WORKSPACE_CONTENT_SEARCH_KEY = "content-search";

/**
 * Several components can observe the same query (the page needs the totals where the
 * result list needs the rows), so an error is logged once per error object, not once per
 * observer.
 */
const loggedErrors = new WeakSet<object>();

interface UseWorkspaceContentSearchOptions {
  /** The search term as typed (already debounced by the caller). */
  searchTerm: string;
  /** Zoom into these workspaces only; undefined searches every workspace the user can see. */
  workspaceIds?: string[];
  workspacePageSize?: number;
  documentsPerWorkspace?: number;
  enabled?: boolean;
}

/**
 * The staircase behind the Workspaces page search (LAR-235): hits grouped per workspace,
 * loaded one page of workspaces at a time ("Vis flere workspaces"). Pages are appended, so
 * `workspaces` is the flat list to render and `totals` describe the whole result.
 */
export const useWorkspaceContentSearch = ({
  searchTerm,
  workspaceIds,
  workspacePageSize = 5,
  documentsPerWorkspace = 3,
  enabled = true,
}: UseWorkspaceContentSearchOptions) => {
  const workspaceService = useItemService((services) => services.workspace);
  const term = searchTerm.trim();
  const isSearchable = term.length >= CONTENT_SEARCH_MIN_LENGTH;

  const query = useInfiniteQuery<WorkspaceContentSearchResult, Error>({
    queryKey: [
      PATH_KEYS.workspace,
      WORKSPACE_CONTENT_SEARCH_KEY,
      term,
      workspaceIds ?? null,
      workspacePageSize,
      documentsPerWorkspace,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await workspaceService.searchWorkspaceContent({
        searchQuery: term,
        workspaceIds,
        workspacePage: typeof pageParam === "number" ? pageParam : 1,
        workspacePageSize,
        documentsPerWorkspace,
      });
      if (response.success) return response.value;
      throw restError(response);
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMoreWorkspaces ? lastPage.workspacePage + 1 : undefined,
    enabled: enabled && isSearchable,
    placeholderData: keepPreviousData,
    retry: shouldRetryQuery,
  });

  // The UI degrades quietly (no count in the switch, an inline message in the list), so the
  // failure has to reach Application Insights from here. The term itself is user content and
  // stays out of the log.
  const { isError, error } = query;
  useEffect(() => {
    if (!isError || !error || loggedErrors.has(error)) return;
    loggedErrors.add(error);
    logger.error("Workspace content search failed", {
      operation: "workspaceContentSearch",
      error,
      searchTermLength: term.length,
      workspaceIds,
      workspacePageSize,
      documentsPerWorkspace,
    });
  }, [
    isError,
    error,
    term.length,
    workspaceIds,
    workspacePageSize,
    documentsPerWorkspace,
  ]);

  const pages = query.data?.pages ?? [];
  const workspaces: WorkspaceContentSearchWorkspace[] = pages.flatMap(
    (page) => page.workspaces
  );
  const lastPage = pages[pages.length - 1];

  return {
    ...query,
    isSearchable,
    workspaces,
    totalDocuments: lastPage?.totalDocuments ?? 0,
    totalWorkspaces: lastPage?.totalWorkspaces ?? 0,
  };
};

export default useWorkspaceContentSearch;
